/**
 * Visualizador do laudo renderizado (E23.F20 — ADR-0030). // ux-neutrality:allow doc-comment
 *
 * Busca `GET /casos/:id/laudo/preview.pdf` (tolerante a rascunho) e renderiza // ux-neutrality:allow doc-comment
 * o documento com pdf.js página a página. Substitui o preview HTML: o perito // ux-neutrality:allow doc-comment
 * confere o laudo "como sai". Re-renderiza quando `reloadToken` muda (ex.: // ux-neutrality:allow doc-comment
 * após confirmar uma alteração). // ux-neutrality:allow doc-comment
 */
import { useEffect, useRef, useState } from 'react';
import { Blueprint, Button, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { getLaudoPreviewPdfBlob, LaudoError } from '@/lib/laudo';
import { LaudoEletronicoError } from '@/lib/laudo_eletronico';
import { loadPdf, type RenderablePdf } from '@/lib/pdf_render';
import type { AxiosInstance } from 'axios';

interface LaudoPdfPreviewProps {
  caseId: string;
  /** Incrementado pelo pai para forçar nova busca + render (ex.: após editar). */
  reloadToken?: number;
  /**
   * De onde vem o PDF. Default: a rota do laudo grafotécnico.
   *
   * E74.F3.T10 — a modalidade eletrônica usa a MESMA rota, mas classifica a
   * recusa por um corpo estruturado próprio; injetar o buscador evita uma
   * segunda cópia do visualizador (pdf.js, paginação, canvas) que divergiria da
   * original na primeira correção.
   */
  fetchPdf?: (client: AxiosInstance, caseId: string) => Promise<Blob>;
}

type ViewerStatus = 'loading' | 'rendering' | 'ready' | 'empty' | 'error';

// E23.F23: o PDF é o protagonista da tela — escala maior (mais nítido) e o
// container abaixo limita a largura de leitura. A página mantém proporção A4.
const RENDER_SCALE = 2.0;

/** "Ainda não há documento" é estado vazio, não falha — nas duas modalidades. */
function ehVazio(err: unknown): boolean {
  if (err instanceof LaudoError) {
    return err.reason === 'narrative_not_ready' || err.reason === 'not_ready';
  }
  if (err instanceof LaudoEletronicoError) return err.reason === 'not_ready';
  return false;
}

export function LaudoPdfPreview({
  caseId,
  reloadToken = 0,
  fetchPdf = getLaudoPreviewPdfBlob,
}: LaudoPdfPreviewProps): JSX.Element {
  const t = ptBR.laudoCaso.preview;
  const client = useApiClient();

  const [status, setStatus] = useState<ViewerStatus>('loading');
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1); // 1-based
  const [doc, setDoc] = useState<RenderablePdf | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Efeito 1 — buscar e parsear o PDF. Define `doc` + `pageCount` e volta à pág. 1.
  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    let loaded: RenderablePdf | null = null;
    setStatus('loading');
    setDoc(null);
    setPageCount(0);
    setCurrentPage(1);
    void (async () => {
      try {
        const blob = await fetchPdf(client, caseId);
        const buffer = await blob.arrayBuffer();
        if (cancelled) return;
        loaded = await loadPdf(buffer);
        if (cancelled) {
          loaded.destroy();
          return;
        }
        setDoc(loaded);
        setPageCount(loaded.numPages);
        setStatus('rendering');
      } catch (err) {
        if (cancelled) return;
        setStatus(ehVazio(err) ? 'empty' : 'error');
      }
    })();
    return () => {
      cancelled = true;
      loaded?.destroy();
    };
  }, [caseId, client, fetchPdf, reloadToken]);

  // Efeito 2 — desenha APENAS a página atual (E23.F23: visualizador paginado).
  useEffect(() => {
    if (!doc) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;
    void (async () => {
      try {
        await doc.renderPage(currentPage, canvas, RENDER_SCALE);
        if (!cancelled) setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doc, currentPage]);

  const goPrev = (): void => setCurrentPage((p) => Math.max(1, p - 1));
  const goNext = (): void => setCurrentPage((p) => Math.min(pageCount, p + 1));

  return (
    <div data-testid="laudo-pdf-preview" data-status={status} className="flex flex-col gap-3">
      {status === 'loading' && (
        <p role="status" className="text-body-sm text-fg-muted">
          {t.documentLoading}
        </p>
      )}
      {status === 'empty' && (
        <p className="text-body-sm text-fg-muted" data-testid="laudo-pdf-empty">
          {t.documentEmpty}
        </p>
      )}
      {status === 'error' && (
        <p role="alert" className="text-body-sm text-danger-700" data-testid="laudo-pdf-error">
          {t.documentError}
        </p>
      )}
      {(status === 'rendering' || status === 'ready') && (
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-sc-3">
          {/* E54.F8.T04: o documento é a figura de blueprint da tela — moldura
              hairline com legenda mono no rodapé, mesma família da página do
              documento da F7. A rolagem fica no filho para alcançar só a chapa
              do documento, deixando a legenda fora dela. */}
          <Blueprint as="figure" className="bg-paper" data-testid="laudo-pdf-frame">
            <div
              role="document"
              aria-label={t.documentLabel}
              aria-busy={status === 'rendering'}
              className="flex max-h-[82vh] w-full justify-center overflow-auto p-sc-3"
              data-testid="laudo-pdf-pages"
            >
              {/* Largura de leitura (≈A4); a página inteira fica grande e legível,
                  rolando só esta página se passar da altura. */}
              <canvas
                ref={canvasRef}
                aria-label={t.pageAlt.replace('{{n}}', String(currentPage))}
                className="h-auto w-full self-start"
                data-testid={`laudo-pdf-page-${currentPage}`}
              />
            </div>
            <figcaption className="border-t border-border px-sc-3 py-sc-2">
              <MonoData variant="label" data-testid="laudo-pdf-frame-caption">
                {t.frameCaption}
              </MonoData>
            </figcaption>
          </Blueprint>
          {/* Paginação: 1 página por vez + anterior/próxima (E23.F23). */}
          <nav
            className="flex items-center justify-center gap-sc-3"
            aria-label={t.documentLabel}
            data-testid="laudo-pdf-pager"
          >
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={goPrev}
              disabled={currentPage <= 1}
              data-testid="laudo-pdf-prev"
            >
              {t.pagePrev}
            </Button>
            {/* Contador de página é dado do documento: mono. */}
            <MonoData variant="label" className="text-fg" data-testid="laudo-pdf-page-indicator">
              {t.pageIndicator
                .replace('{{n}}', String(currentPage))
                .replace('{{total}}', String(pageCount))}
            </MonoData>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={goNext}
              disabled={currentPage >= pageCount}
              data-testid="laudo-pdf-next"
            >
              {t.pageNext}
            </Button>
          </nav>
        </div>
      )}
    </div>
  );
}
